import { get } from 'lodash';
import React from 'react';

import { TextField } from 'Containers/App/types';
import { ContentfulCustomMessage } from 'ContentfulDefaults/types/_shared';
import { ContentfulPlanDisclaimer, PlanCustomization } from 'ContentfulDefaults/types/customizations';
import { ContentfulTextNote } from 'ContentfulWrappers/ContentfulTextNote';
import { useTextContext } from 'Contexts/textContext';
import InfoBar from 'DesignLibrary/molecules/InfoBar';
import Plan from 'Models/plan';
import { createHraTooltip, createHsaTooltip, createSihraTooltip } from 'Utils/createTooltips';
import { toDollars } from 'Utils/helpers';

import { AccountInfobar } from './AccountInfobar';

interface DisclaimersProps {
  plan: Plan;
  employerHsaContribution?: number | null;
  maximumAvailableAnnualSihraContribution: number;
  isEntered: boolean;
  hsaContributionLimit: number;
  hdhpMinDeductible?: number | null;
  isNoDeductibleCallOutAvailable?: boolean;
}

export const Disclaimers = ({
  plan,
  employerHsaContribution,
  maximumAvailableAnnualSihraContribution,
  isEntered,
  hsaContributionLimit,
  hdhpMinDeductible,
  isNoDeductibleCallOutAvailable,
}: DisclaimersProps) => {
  const { planId, hsaEligible, hraEligible, employerHraContribution, annualSihraDollarsUsed } = plan || {};

  const hraIsLimitedToSelectProviders = plan?.hraHasLimitedProviderReimbursement === true;
  // NOTE: today's config software only sets hra_post_deductible_with_hsa when
  // limited_provider_reimbursement is also true, so isHsaHraCombo currently implies
  // hraIsLimitedToSelectProviders. The explicit && guards against that invariant changing.
  const isHsaHraComboPlusSelectProviderHra = plan?.isHsaHraCombo && hraIsLimitedToSelectProviders;

  // Get disclaimers from Contentful
  const { retrieveContentfulData } = useTextContext();
  const customizations = retrieveContentfulData<PlanCustomization[]>('plan_customizations', []);
  const planCustomizationsIndex = customizations.findIndex((item) => item.plan_id === planId);
  const planCustomizations = planCustomizationsIndex >= 0 ? customizations[planCustomizationsIndex] : null;

  const disclaimers: ContentfulPlanDisclaimer[] = get(planCustomizations, 'result_page_disclaimers', []);

  const renderHsaBar = () => {
    if (hsaEligible) {
      const hsaTooltip = {
        ...createHsaTooltip(
          plan.isEmployerMatching,
          plan.calcEmployerHsaContribution(hsaContributionLimit),
          plan.employerHsaContributionLimit,
          plan.employerHsaPercentageMatch,
        ),
        underline: '',
      };

      // When the plan also has a select-provider HRA usable post-deductible alongside the HSA,
      // append HRA-specific copy after the standard HSA contribution text instead of rendering
      // a separate HRA bar.
      const hsaAddtlInfo = retrieveContentfulData<ContentfulCustomMessage[]>(
        'health_section.recommendation.hsa_addtl_info',
        [],
      );
      const garnerHraIdx = hsaAddtlInfo.findIndex(
        (msg) => msg.message_id === 'health_rec_hsa_addtl_info_garner_hra',
      );
      const hsaHraComboProps =
        isHsaHraComboPlusSelectProviderHra && garnerHraIdx >= 0
          ? {
              additionalContentField:
                `health_section.recommendation.hsa_addtl_info[${garnerHraIdx}].text` as TextField,
              additionalContentVars: [toDollars(hdhpMinDeductible), toDollars(employerHraContribution)],
            }
          : {};

      if (plan.isEmployerMatching) {
        return (
          <div data-testid="hsa-info-bar-with-employer-match">
            <AccountInfobar
              type="hsa"
              field="health_section.recommendation.hsa_info_with_employer_match"
              vars={[
                `${plan.employerHsaPercentageMatch}%` || 0,
                `$${plan.employerHsaContributionLimit}` || 0,
              ]}
              tooltips={[hsaTooltip, hsaTooltip]}
              {...hsaHraComboProps}
            />
          </div>
        );
      }
      if (employerHsaContribution && employerHsaContribution > 0) {
        return (
          <div data-testid="hsa-info-bar-with-contribution">
            <AccountInfobar
              type="hsa"
              field="health_section.recommendation.hsa_info_with_contribution"
              vars={[toDollars(employerHsaContribution)]}
              tooltips={[hsaTooltip]}
              {...hsaHraComboProps}
            />
          </div>
        );
      }

      return (
        <div data-testid="hsa-info-bar-without-contribution">
          <AccountInfobar type="hsa" field="health_section.recommendation.hsa_info" {...hsaHraComboProps} />
        </div>
      );
    }

    return null;
  };

  const renderHraBar = () => {
    if (hraEligible) {
      // Suppress HRA bar when the plan also offers an HSA
      // Either a misconfig OR HSA bar will contain HRA info
      if (hsaEligible) {
        return null;
      }

      const noContribution = !employerHraContribution
        ? 'health_section.eligibility_text.hra.standardPlan'
        : null;

      const hraTooltip = {
        ...createHraTooltip(plan.employerHraContribution),
        underline: '',
      };

      let hraField: TextField = 'health_section.recommendation.hra_info_with_contribution';
      if (hraIsLimitedToSelectProviders) {
        const hraSelectProvidersMessages = retrieveContentfulData<ContentfulCustomMessage[]>(
          'health_section.recommendation.hra_info_with_contrib_for_select_providers',
          [],
        );
        const garnerIdx = hraSelectProvidersMessages.findIndex(
          (msg) => msg.message_id === 'hra_info_w_contrib_select_providers_garner',
        );
        if (garnerIdx >= 0) {
          hraField =
            `health_section.recommendation.hra_info_with_contrib_for_select_providers[${garnerIdx}]` as TextField;
        }
      }

      return (
        <div data-testid="hra-info-bar-with-contribution">
          <AccountInfobar
            type="hra"
            field={hraField}
            vars={[toDollars(employerHraContribution)]}
            tooltips={[hraTooltip]}
            textOverride={noContribution}
          />
        </div>
      );
    }

    return null;
  };

  const shouldRenderSihraBar =
    isEntered &&
    annualSihraDollarsUsed &&
    annualSihraDollarsUsed > 0 &&
    maximumAvailableAnnualSihraContribution > 0;

  const renderSihraBar = () => {
    if (shouldRenderSihraBar) {
      const sihraTooltip = {
        ...createSihraTooltip(maximumAvailableAnnualSihraContribution),
        underline: '',
      };
      return (
        <div data-testid="sihra-info-bar-with-contribution">
          <AccountInfobar
            type="hra"
            field="health_section.recommendation.sihra_info_with_contribution"
            vars={[toDollars(maximumAvailableAnnualSihraContribution)]}
            tooltips={[sihraTooltip]}
          />
        </div>
      );
    }

    return null;
  };

  const renderTextNote = () => {
    if (planCustomizations) {
      return <ContentfulTextNote sectionKey="plan_customizations" sectionIndex={planCustomizationsIndex} />;
    }
    return null;
  };

  const disclaimerBars = disclaimers.map((disclaimer, disclaimerIdx) => (
    <InfoBar
      color={disclaimer.barColor || 'yellow'}
      header={disclaimer.header}
      text={disclaimer.description}
      key={`disclaimer-${disclaimerIdx}`}
      showAccent={false}
    />
  ));

  const renderNoDeductibleBars = () => {
    const callOutText =
      plan?.carrierName === 'Surest' ? 'no_deductible_info_surest_text' : 'no_deductible_info';
    const noDeductibleField =
      `health_section.recommendation.no_deductible_plan_callout.${callOutText}` as TextField;

    return (
      <div data-testid="no-deductible-info-bar">
        <AccountInfobar type="no_deductible" textOverride={noDeductibleField} />
      </div>
    );
  };

  return (
    <>
      {isNoDeductibleCallOutAvailable && renderNoDeductibleBars()}
      {renderHsaBar()}
      {renderHraBar()}
      {renderSihraBar()}
      {disclaimerBars}
      {renderTextNote()}
    </>
  );
};
